/* eslint-disable no-nested-ternary */
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CheckmarkCircleNextIcon } from '@glu/icons-react/lib';
import useStyles from './DetailsTable.styles';
import LocaleContext from '../../../locale';
import { PROCESS_MODES_NAMES } from '../utils';
import { MAP_FORMAT } from '../CreateACHImportMap/utils.mapFormats';
import {
  API_TRAN_TYPE_ROW_NAME_VOCABULARY,
  API_TRAN_TYPE_ROW_VOCABULARY,
  getTableFields,
  TABLE_FIELDS,
  TRAN_TYPE_ROW_VOCABULARY
} from '../CreateACHImportMap/constants';
import { getImportFileInputsForField } from '../CreateACHImportMap/utils';

const getTableCellClass = (turnOnCheckboxes, availableFixedClass, classes) => {
  return turnOnCheckboxes
    ? `${classes.updateCell} ${availableFixedClass}`
    : `${classes.cell} ${availableFixedClass}`;
};

const TableComponent = ({ data, mapFormat, processMode }) => {
  const classes = useStyles();
  const locale = useContext(LocaleContext);
  const tableFields = getTableFields(locale);

  const mapData = useMemo(
    // brings fieldName from api format to table
    () => data.map((item) => ({ ...item, fieldName: TABLE_FIELDS[item.fieldName] })),
    [data]
  );

  const hasImportInputs = useMemo(
    () =>
      Boolean(
        mapData
          ?.filter(
            (field) =>
              field.fieldName === TABLE_FIELDS.ACCOUNT_TYPE ||
              field.fieldName === TABLE_FIELDS.TRAN_TYPE
          )
          ?.find((field) => Object.keys(API_TRAN_TYPE_ROW_VOCABULARY)?.some((key) => field[key]))
      ),
    [mapData]
  );

  const turnOnCheckboxes = useMemo(
    () =>
      processMode === PROCESS_MODES_NAMES.UPDATE_ONLY ||
      processMode === PROCESS_MODES_NAMES.ADD_AND_UPDATE,
    [processMode]
  );

  const availableFixedClass = useMemo(
    () => (mapFormat === MAP_FORMAT.FIXED ? 'fixed-map' : ''),
    [mapFormat]
  );
  const cellClass = useMemo(
    () =>
      `${getTableCellClass(turnOnCheckboxes, availableFixedClass, classes)} ${
        hasImportInputs ? 'import-file' : ''
      }`,
    [turnOnCheckboxes, availableFixedClass, classes, hasImportInputs]
  );

  const getAddValue = (field) => {
    const foundItem = mapData.find((item) => item.fieldName === field.id);
    if (field.id === TABLE_FIELDS.AMOUNT) {
      return foundItem?.amountDecimal;
    }
    return foundItem?.replacementValue;
  };

  return (
    <div
      className={classnames(
        classes.wrapper,
        !availableFixedClass && !turnOnCheckboxes ? 'delimited-map add-only' : '',
        hasImportInputs ? 'import-file' : ''
      )}
    >
      <div className={classes.header}>
        <div className={cellClass}>{locale['wireImports.fieldName']}</div>
        {turnOnCheckboxes && (
          <>
            <div
              className={classnames(
                classes.updateCheckboxCell,
                availableFixedClass,
                hasImportInputs ? 'import-file' : ''
              )}
            >
              {locale['wireImports.match']}
            </div>
            <div
              className={classnames(
                classes.updateCheckboxCell,
                availableFixedClass,
                hasImportInputs ? 'import-file' : ''
              )}
            >
              {locale['wireImports.update']}
            </div>
          </>
        )}
        {mapFormat === MAP_FORMAT.FIXED ? (
          <>
            <div className={cellClass}>{locale['wireImports.startPosition']}</div>
            <div className={cellClass}>{locale['common.length']}</div>
          </>
        ) : (
          <div className={cellClass}>{locale['common.position']}</div>
        )}
        {hasImportInputs && (
          <div className={cellClass}>{locale['wireImports.valueInImportFile']}</div>
        )}
        <div className={cellClass}>{locale['common.value']}</div>
      </div>
      <div className={classes.body}>
        {tableFields.map((field) => {
          const fieldData = mapData.find((item) => item.fieldName === field.id);

          if (!fieldData) return null;

          const importFileInputs = getImportFileInputsForField(fieldData);

          return (
            <div key={field.id} className={classes.row}>
              <div
                className={classnames(cellClass, {
                  [classes.optionalName]:
                    field.optional && processMode !== PROCESS_MODES_NAMES.UPDATE_ONLY
                })}
              >
                {field.name}
              </div>
              {turnOnCheckboxes && (
                <>
                  <div
                    className={classnames(
                      classes.updateCheckboxCell,
                      availableFixedClass,
                      hasImportInputs ? 'import-file' : ''
                    )}
                  >
                    {fieldData?.matchFlag ? (
                      <CheckmarkCircleNextIcon className={classes.checkmarkWrapper} />
                    ) : (
                      ''
                    )}
                  </div>
                  <div
                    className={classnames(
                      classes.updateCheckboxCell,
                      availableFixedClass,
                      hasImportInputs ? 'import-file' : ''
                    )}
                  >
                    {fieldData?.updateFlag ? (
                      <CheckmarkCircleNextIcon className={classes.checkmarkWrapper} />
                    ) : (
                      ''
                    )}
                  </div>
                </>
              )}
              {mapFormat === MAP_FORMAT.FIXED ? (
                <>
                  <div className={cellClass}>{fieldData?.startPosition || '-'}</div>
                  <div className={cellClass}>{fieldData?.fieldLength || '-'}</div>
                </>
              ) : (
                <div className={cellClass}>{fieldData?.fieldPosNumber || '-'}</div>
              )}
              {hasImportInputs ? (
                Object.keys(importFileInputs).length ? (
                  <div className={cellClass}>
                    {Object.keys(importFileInputs).map((key) => (
                      <div key={key}>
                        <span>{API_TRAN_TYPE_ROW_NAME_VOCABULARY(locale)[key]} : </span>
                        {fieldData[TRAN_TYPE_ROW_VOCABULARY[key]] || '-'}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={cellClass}>-</div>
                )
              ) : null}
              <div className={cellClass}>{getAddValue(field) || '-'}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

TableComponent.propTypes = {
  data: PropTypes.shape({}),
  processMode: PropTypes.string.isRequired,
  mapFormat: PropTypes.string.isRequired
};

TableComponent.defaultProps = {
  data: {}
};
export default TableComponent;
